/**
 * The protocol between the project and its clients (browser tabs, and later
 * the MCP server and the running-game bridge). The dev server carries it
 * over a WebSocket; the browser version carries it in the page. Either way a
 * client sends commands and receives the resulting changes.
 */
import type { FileMove, Op } from '@animo-editor/format';
import { CommandRejected, type ChangeEvent, type CompressRequest, type EditorProject, type ImportedFile } from './project.js';

/** Messages a client sends. `req` is echoed in the reply. */
export type ClientMessage =
  | { t: 'apply'; req: number; label: string; ops: Op[] }
  | { t: 'import'; req: number; label: string; files: ImportedFile[]; ops: Op[] }
  | { t: 'move'; req: number; label: string; moves: FileMove[] }
  | { t: 'compress'; req: number; label: string; request: CompressRequest; ops: Op[] }
  | { t: 'source'; req: number; path: string }
  | { t: 'compressState'; req: number }
  | { t: 'generate'; req: number; files: ImportedFile[]; stamps: Record<string, string> }
  | { t: 'list'; req: number }
  | { t: 'relink'; req: number; label: string; relinks: FileMove[] }
  | { t: 'undo'; req: number }
  | { t: 'redo'; req: number }
  | { t: 'save'; req: number; files?: string[] }
  | { t: 'resolve'; req: number; file: string; take: 'mine' | 'disk' };

/** Messages the project sends. */
export type ServerMessage =
  | { t: 'snapshot'; client: string; rev: number; files: Record<string, unknown>; dirty: string[]; conflicts: string[]; history: { undo: string[]; redo: string[] }; relinks: FileMove[] }
  | { t: 'relinks'; relinks: FileMove[] }
  | { t: 'ack'; req: number; rev: number; saved?: string[]; files?: string[]; data?: string; sources?: Record<string, string | null>; stamps?: Record<string, string> }
  | { t: 'generated'; files: string[] }
  | { t: 'reject'; req: number; message: string; problems?: unknown[] }
  | { t: 'change'; rev: number; kind: string; label: string; source: string; ops: Op[]; dirty: string[]; moves?: FileMove[] }
  | { t: 'saved'; rev: number; files: string[] }
  | { t: 'conflict'; file: string }
  | { t: 'history'; undo: string[]; redo: string[] }
  | { t: 'error'; file?: string; message: string };

export interface Session {
  /** Handles one message from the client. */
  receive(msg: ClientMessage): Promise<void>;
  /** Stops sending the project's events to the client. */
  close(): void;
}

/**
 * Connects one client to a project: sends it a snapshot, runs its commands,
 * and forwards every change. `send` delivers a message to the client.
 */
export function openSession(project: EditorProject, client: string, send: (msg: ServerMessage) => void): Session {
  const listeners = {
    change: (e: ChangeEvent) => send({ t: 'change', ...e }),
    saved: (e: { files: string[]; rev: number }) => send({ t: 'saved', ...e }),
    conflict: (e: { file: string }) => send({ t: 'conflict', ...e }),
    history: (e: { undo: string[]; redo: string[] }) => send({ t: 'history', ...e }),
    error: (e: { file?: string; message: string }) => send({ t: 'error', ...e }),
    relinks: (e: { relinks: FileMove[] }) => send({ t: 'relinks', ...e }),
    generated: (e: { files: string[] }) => send({ t: 'generated', ...e }),
  };
  project.on('change', listeners.change);
  project.on('saved', listeners.saved);
  project.on('conflict', listeners.conflict);
  project.on('history', listeners.history);
  project.on('error', listeners.error);
  project.on('relinks', listeners.relinks);
  project.on('generated', listeners.generated);
  send({ t: 'snapshot', client, ...project.snapshot() });

  return {
    async receive(msg) {
      const req = typeof msg.req === 'number' ? msg.req : 0;
      try {
        switch (msg.t) {
          case 'apply':
            send({ t: 'ack', req, rev: project.apply(String(msg.label ?? 'Edit'), msg.ops, client).rev });
            return;
          case 'import':
            send({ t: 'ack', req, rev: (await project.importFiles(String(msg.label ?? 'Import'), msg.files, msg.ops, client)).rev });
            return;
          case 'move':
            send({ t: 'ack', req, rev: (await project.moveFiles(String(msg.label ?? 'Move'), msg.moves, client)).rev });
            return;
          case 'compress':
            send({ t: 'ack', req, rev: (await project.compressFile(String(msg.label ?? 'Compress'), msg.request, msg.ops, client)).rev });
            return;
          case 'source':
            send({ t: 'ack', req, rev: project.rev, data: await project.readSource(String(msg.path)) });
            return;
          case 'compressState': {
            const state = await project.compressState();
            send({ t: 'ack', req, rev: project.rev, ...state });
            return;
          }
          case 'generate':
            send({ t: 'ack', req, rev: project.rev, files: await project.writeGenerated(msg.files, msg.stamps) });
            return;
          case 'list':
            send({ t: 'ack', req, rev: project.rev, files: await project.listFiles() });
            return;
          case 'relink':
            send({ t: 'ack', req, rev: (await project.relink(String(msg.label ?? 'Fix links'), msg.relinks, client)).rev });
            return;
          case 'undo':
            await project.undo(client);
            send({ t: 'ack', req, rev: project.rev });
            return;
          case 'redo':
            await project.redo(client);
            send({ t: 'ack', req, rev: project.rev });
            return;
          case 'save': {
            const saved = await project.save(msg.files);
            send({ t: 'ack', req, rev: project.rev, saved });
            return;
          }
          case 'resolve':
            await project.resolveConflict(msg.file, msg.take === 'disk' ? 'disk' : 'mine', client);
            send({ t: 'ack', req, rev: project.rev });
            return;
          default:
            send({ t: 'reject', req, message: `Unknown message type "${(msg as { t?: unknown }).t}"` });
        }
      } catch (e) {
        const problems = e instanceof CommandRejected ? e.problems : undefined;
        send({ t: 'reject', req, message: e instanceof Error ? e.message : String(e), ...(problems && { problems }) });
      }
    },
    close() {
      project.off('change', listeners.change);
      project.off('saved', listeners.saved);
      project.off('conflict', listeners.conflict);
      project.off('history', listeners.history);
      project.off('error', listeners.error);
      project.off('relinks', listeners.relinks);
      project.off('generated', listeners.generated);
    },
  };
}
